import type { ReactNode } from 'react';
import { cx } from '../cx';

/**
 * THE RECORD HEADER, and Apple ships no part for it.
 *
 * SO IT IS DERIVED, AND EVERY PART OF IT NAMES A PUBLISHED SOURCE (wave 5, B-6; the source
 * audit of 2026-09-14 section 3 read this band as OPEN because its caption ended on a
 * screenshot, and a measured value is refused rather than counted). Nothing here is read off
 * a picture:
 *
 *  - THE TILE. kit: `Forms/Rows - Leading/Sidebar Icon/Service Icon 32pt`, with the same
 *    master's row height and form inset for the stack beside it.
 *  - THE NAME'S SIZE. kit: the macOS Title1, 22, which the product carries as `--text-lg`.
 *  - THE LABEL COLOUR. hig:
 *    https://developer.apple.com/design/human-interface-guidelines/labels, the secondary
 *    label role.
 *  - WHY THE BAND EXISTS AT ALL, and why it stops at six values. hig:
 *    https://developer.apple.com/design/human-interface-guidelines/layout, "give essential
 *    information sufficient space".
 *  - THE COMPOSITION - a picture, the name in bold, a secondary line, then the details.
 *    support: https://support.apple.com/guide/contacts/view-contacts-adrb003645bb/mac (a Mac
 *    contact card) and support:
 *    https://support.apple.com/guide/mail/read-and-respond-to-emails-mlhlp1010/mac (the
 *    message header), where the header sits on the SAME surface as the body, separated from
 *    it by space and a hairline rather than by a frame.
 *  - THE VALUE GRID, label over value and several to a row. web:
 *    https://support.apple.com/guide/apple-school-manager/view-device-information-apdc6a44b6e2/web,
 *    which is Apple's own record detail.
 *
 * WHAT IS STILL OPEN is not the construction but the CONTENT: which six values each object
 * shows. That is product configuration (D32, `decisions/CRM-REDESIGN-CORE-PAGES-2026-09-09.md`)
 * and the settings row that edits it is not built.
 *
 * WHAT IT IS NOT: a card. "Apple does not box the item's header"
 * (`spec/screens/record.md` section 7) is the first item of this screen's own list,
 * and it is the thing the 2026-09-09 mockups got wrong. No border, no fill, no
 * shadow - a hairline underneath and nothing else.
 *
 * AT MOST SIX VALUES, because the layout rule this band exists for ("give essential
 * information sufficient space", rulebook H2) stops being true at the seventh. The
 * caller decides WHICH six; the component enforces the six.
 */
export interface IdentityHighlight {
  readonly label: string;
  /**
   * `null` is a real answer and it renders as a dash in the tertiary label colour
   * (`spec/screens/record.md` section 4, Partial): an empty highlight says "nothing
   * here yet", where a missing row would say "this record has no such field".
   */
  readonly value: ReactNode | null;
}

export interface IdentityBandProps {
  readonly name: string;
  /** The object's own name, under the record's: "Agency", "Lead", "Call". */
  readonly secondary?: ReactNode | undefined;
  /** The object tile, supplied by the caller so this component owns no artwork. */
  readonly tile?: ReactNode | undefined;
  readonly highlights?: readonly IdentityHighlight[] | undefined;
  /**
   * The band's trailing slot. It renders NOTHING until the assistant package
   * (`spec/screens/record.md` section 2 item 4: "the next step slot"), and an empty
   * slot draws no box, so the band is unchanged until something is passed.
   */
  readonly trailing?: ReactNode | undefined;
  readonly className?: string | undefined;
}

/** The band holds at most six values; the seventh is a design error, not a scroll. */
export const IDENTITY_HIGHLIGHT_LIMIT = 6;

export function IdentityBand({
  name,
  secondary,
  tile,
  highlights,
  trailing,
  className,
}: IdentityBandProps) {
  const shown = (highlights ?? []).slice(0, IDENTITY_HIGHLIGHT_LIMIT);
  return (
    <div className={cx('record-band', className)}>
      <div className="record-band-identity">
        {tile}
        <div className="min-w-0">
          {/*
            The name repeats the window title ONCE, at the kit's Title1 size: the
            toolbar title is the orientation and this is the content's own heading,
            which is what Contacts and Notes both do. It is not an `<h1>` - the
            toolbar's title is the page's one h1 and a second one would announce two
            page titles to a screen reader.
          */}
          <p className="record-band-name">{name}</p>
          {secondary !== undefined && <p className="record-band-secondary">{secondary}</p>}
        </div>
        {trailing !== undefined && <div className="record-band-trailing">{trailing}</div>}
      </div>
      {shown.length > 0 && (
        <dl className="record-band-values">
          {shown.map((highlight) => (
            <div key={highlight.label} className="record-band-pair">
              <dt className="record-band-label">{highlight.label}</dt>
              <dd
                className={cx('record-band-value', highlight.value === null && 'text-ink-subtle')}
              >
                {highlight.value ?? '—'}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}
